import { LocalAudioTrack } from 'livekit-client';
import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import Hls from 'hls.js';
import {
  getStore,
  usePlayerStore,
  useExamSessionStore,
} from '../../../stores/StoreContext';
import { View } from '@instructure/ui';
import { Parser } from 'm3u8-parser';
import moment from 'moment';
import CONSTANTS from '../../../config/constants';
import AudioVisualizer from '../../SessionDetails/Communication/AudioVisualizer';

const HlsVideoPlayer = forwardRef(
  (
    { url, startTime = 0, endTime, onEndTime, hlsConfig = {}, videoProps = {} },
    ref
  ) => {
    const videoRef = useRef(null);
    const hlsInstanceRef = useRef(null);

    const PlayerStore = getStore('playerStore');
    const ExamSessionStore = getStore('examSessionStore');

    const [audioTrack, setAudioTrack] = useState(null);

    const playbackSpeed = usePlayerStore((state) => state.playbackSpeed);
    const currentPlaybackTime = usePlayerStore((state) => state.currentTime);
    const playing = usePlayerStore((state) => state.playing);
    const recordingStatus = useExamSessionStore(
      (state) => state.recordingStatus
    );

    class RawLoader extends Hls.DefaultConfig.loader {
      load(context, config, callbacks) {
        super.load(context, config, {
          onSuccess: (response, stats, ctx, xhr) => {
            if (
              typeof response.data === 'string' &&
              recordingStatus !== CONSTANTS.RECORDING_STATUS.COMPLETE
            ) {
              var parser = new Parser();
              parser.push(response.data);
              parser.end();

              var parsedManifest = parser.manifest;

              // update the recording end date if the HLS file is still being updated...
              if (!parsedManifest.endList) {
                const lastSegment =
                  parsedManifest.segments[parsedManifest.segments.length - 1];
                const recordingEndDate = moment
                  .utc(lastSegment.dateTimeString)
                  .add(lastSegment.duration, 'seconds')
                  .toISOString();
                ExamSessionStore.setRecordingMetadata({
                  endDate: recordingEndDate,
                });
              }
            }
            // then hand back to hls.js to parse as usual:
            callbacks.onSuccess(response, stats, ctx, xhr);
          },
          onError: callbacks.onError,
          onTimeout: callbacks.onTimeout,
          onProgress: callbacks.onProgress,
        });
      }
    }

    const isPlaying = () => {
      const video = videoRef.current;
      if (!video) {
        return false;
      }
      if (!video.paused && !video.ended && video.readyState > 2) {
        return true;
      } else {
        return false;
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        play: () => videoRef.current?.play(),
        pause: () => videoRef.current?.pause(),
        setPlaybackRate: (rate) => {
          if (videoRef.current) {
            videoRef.current.playbackRate = rate;
          }
        },
        loadSource: (newUrl) => {
          if (Hls.isSupported() && hlsInstanceRef.current) {
            hlsInstanceRef.current.loadSource(newUrl);
          } else if (videoRef.current) {
            videoRef.current.src = newUrl;
          }
        },
        // Expose the hls.js instance
        getHlsInstance: () => hlsInstanceRef.current,
      }),
      []
    );

    useEffect(() => {
      const video = videoRef.current;

      // set the HLS Video in the player store so it can be used in other places...
      PlayerStore.setHlsVideoRef(videoRef);

      const handleTimeUpdate = () => {
        const currentSecond = Math.floor(video.currentTime);
        const videoDuration = Math.ceil(video.duration);
        // TODO: Make sure this is not calling too much...
        if (isPlaying()) {
          PlayerStore.setCurrentTime(currentSecond);
        }
      };

      /**
       * Handle video ended event - ensures final time is set even at high playback speeds
       * This fires when the video naturally ends, regardless of timeupdate frequency
       */
      const handleEnded = () => {
        const videoDuration = Math.ceil(video.duration);

        PlayerStore.setPlaying(false);
        PlayerStore.setCurrentTime(videoDuration); // Set to exact duration
        video.pause();
        if (onEndTime) {
          onEndTime();
        }
      };

      /**
       * Handle seeked event - fires when seeking operation completes
       * Clear the seeking state when video is ready to play at new position
       */
      const handleSeeked = () => {
        // Always clear seeking state when seeked event fires
        PlayerStore.setIsSeeking(false);
      };

      if (Hls.isSupported()) {
        const finalConfig = {
          startPosition: startTime,
          loader: RawLoader,
          ...hlsConfig,
        };
        const hls = new Hls(finalConfig);
        hlsInstanceRef.current = hls;
        // Hide all video controls
        video.controls = true;

        hls.loadSource(url);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          try {
            //video.play();
          } catch (e) {
            console.warn(
              'Cannot autoplay video until user interacts with the document.'
            );
          }
        });

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('seeked', handleSeeked);

        return () => {
          video.removeEventListener('timeupdate', handleTimeUpdate);
          video.removeEventListener('ended', handleEnded);
          video.removeEventListener('seeked', handleSeeked);
          hls.destroy();
        };
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url;
        video.currentTime = startTime;
        PlayerStore.setDuration(Math.floor(video.duration));

        video.addEventListener('loadedmetadata', () => {
          video.play();
        });

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('seeked', handleSeeked);
        return () => {
          video.removeEventListener('timeupdate', handleTimeUpdate);
          video.removeEventListener('ended', handleEnded);
          video.removeEventListener('seeked', handleSeeked);
        };
      }
    }, []);
    //[url , startTime, endTime, hlsConfig, onEndTime]
    useEffect(() => {
      const video = videoRef.current;

      if (playbackSpeed !== video.playbackRate) {
        // if the playback rate is 100.0, this means we are in smart mode, load as 1 to start...
        if (playbackSpeed === '100.0') {
          video.playbackRate = 1;
        } else {
          video.playbackRate = playbackSpeed;
        }

        if (playing && !isPlaying()) {
          video.play();
        }
      }
    }, [playbackSpeed]);

    useEffect(() => {
      const video = videoRef.current;

      if (playing && !isPlaying()) {
        try {
          const mediaStream = video.captureStream();
          const audioTrack = mediaStream.getAudioTracks()[0];

          // Only create LocalAudioTrack if audioTrack exists
          if (audioTrack) {
            const localAudio = new LocalAudioTrack(audioTrack, {
              name: 'hls-audio',
            });
            setAudioTrack(localAudio);
          }
        } catch (error) {
          console.warn('Failed to capture audio stream:', error);
        }

        video.play();
        //requestAnimationFrame(drawVideos);
      } else if (!playing && isPlaying()) {
        video.pause();
      }
    }, [playing]);

    return (
      <video
        ref={videoRef}
        style={{ height: '0px', width: '0px' }}
        {...videoProps}
      />
    );
  }
);

export default HlsVideoPlayer;
